/**
 * The outcome state shared by both POST channels (the contact form and the
 * newsletter signup).
 *
 * One closed union rather than a status plus a set of booleans. Every member is
 * mutually exclusive by construction, so no combination of writes can render
 * two banners at once or leave the submit button disabled with nothing on
 * screen explaining why. `rate-limited` in particular used to live outside the
 * union as its own boolean beside the status, which made states like
 * ("success" + rateLimited) representable and left their unreachability resting
 * on the ORDER of two setState calls in the submit handler - an invariant
 * nothing checked and a second writer (the auto-clear effect) never consulted.
 *
 * Declared here rather than once per component because both channels drive the
 * same `withTimeout` / `TimeoutError` / cooldown contract, so the two copies
 * were meant to stay identical with nothing enforcing that they did.
 *
 * "Shared" means the contract is shared, NOT that every member is reachable
 * from both callers: `rejected` is currently assigned only by the contact form.
 * See its own note below for why the newsletter's refusal is a different case
 * rather than a missing assignment.
 */
export type SubmitStatus =
  /** Nothing in flight, nothing to report. */
  | 'idle'
  /** A request is in flight; the submit control is disabled. */
  | 'submitting'
  /** The provider confirmed. */
  | 'success'
  /** The attempt failed in a way a retry might fix; generic copy. */
  | 'error'
  /**
   * The deadline elapsed. Distinct from `error` because it says nothing about
   * whether the provider accepted the request, so the copy asks the user to
   * wait rather than to retry immediately.
   */
  | 'timeout'
  /** A resend arrived inside the cooldown window and was not sent. */
  | 'rate-limited'
  /**
   * The provider answered, understood the request, and refused it - a revoked
   * or rotated key, an unverified domain, an exhausted quota.
   *
   * Distinct from `error` because a retry cannot fix it: the generic copy tells
   * the visitor to try again, which for this class will fail identically
   * forever, and one shared analytics event makes a permanently dead channel
   * indistinguishable from a transient blip. The build gate cannot catch it
   * either - the key is present and is not a placeholder, just wrong.
   *
   * Assigned by the contact form only, and that asymmetry is deliberate. This
   * member describes a refusal of the CHANNEL: the cause is the same for every
   * visitor, so the copy is a constant and carries no per-attempt detail. The
   * newsletter's refusal is a refusal of the ADDRESS - a typo, a blocked
   * domain, one already on the list - which is specific to what the reader
   * typed and needs detail this member cannot hold. That component therefore
   * keeps a separate message string beside the status. Making it assign
   * `rejected` would either discard the address-specific reason or require a
   * `{ status: 'rejected', detail: string }` variant; the second is the real
   * fix and is recorded in the deferred list rather than done at merge time.
   */
  | 'rejected';

/** Cooldown window shared by both channels, in seconds. */
export const SUBMIT_COOLDOWN_SECONDS = 30;

/**
 * Abort reason both channels pass when a NEWER submit replaces an in-flight one.
 *
 * Without it, `signal.aborted` conflates two cases the handlers must treat
 * differently: an unmount (the visitor navigated away mid-request - the send is
 * lost and nobody is left to tell, so it is worth counting) and a supersede (the
 * component is still on screen and a newer attempt already owns the outcome, so
 * counting it would double-count one visitor's single conversion).
 */
export const SUPERSEDED_ABORT_REASON = 'superseded-by-newer-submit';

/**
 * The in-flight submit a channel can still cancel, plus WHY it was cancelled.
 *
 * `superseded` is tracked here rather than read back from
 * `AbortSignal.reason` because the reason only round-trips where
 * `abort(reason)` is honoured; where it is ignored the signal carries a generic
 * `DOMException` instead, and a superseded attempt would then be misread as an
 * unmount and counted as an abandoned conversion - inflating the very metric
 * the distinction exists to make trustworthy. The reason is still passed to
 * `abort()` so an operation inspecting `signal.reason` sees it, but nothing
 * BRANCHES on it.
 */
export interface PendingSubmit {
  readonly controller: AbortController;
  superseded: boolean;
}

/** Cancel `pending` because a newer submit has replaced it. */
export function supersede(pending: PendingSubmit | null): void {
  if (!pending) return;
  pending.superseded = true;
  pending.controller.abort(SUPERSEDED_ABORT_REASON);
}

/** Start tracking a fresh attempt. */
export function beginSubmit(): PendingSubmit {
  return { controller: new AbortController(), superseded: false };
}
